/**
 * The untouched response, for when the rendering is not the question.
 *
 * Rendered with `textContent` semantics - JSX inserts the string as a text node, never
 * as markup - because this payload is untrusted input from a monitored service. It is
 * also the escape hatch that keeps the dashboard honest: if a panel above looks wrong,
 * the bytes it was built from are one click away.
 */

import { Check, Copy } from 'lucide-react'
import { useState } from 'react'

export function RawPayload({ payload }: { payload: Record<string, unknown> }) {
  const [copied, setCopied] = useState(false)
  const text = JSON.stringify(payload, null, 2)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      /* Clipboard needs a secure context; the text is selectable either way. */
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 rounded border bg-surface px-1.5 py-1 text-[11px] text-ink-secondary hover:bg-surface-sunken"
      >
        {copied ? <Check size={11} aria-hidden /> : <Copy size={11} aria-hidden />}
        {copied ? 'Copied' : 'Copy'}
      </button>

      <pre
        className="max-h-96 overflow-auto px-4 py-3 text-[11px] leading-relaxed text-ink-secondary"
        style={{ backgroundColor: 'var(--color-surface-sunken)' }}
      >
        <code>{text}</code>
      </pre>
    </div>
  )
}
